import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';

import {
	ISearchPillProps,
	ISearchProps,
	ISearchResult,
	TOnClearCallback,
	TResultItemClickCallback,
	TSearchActionCallback,
	TSearchCallback,
	TSelectedItemChangeCallback,
} from './models/index.js';

export default class SearchStore {
	term = '';
	results: ISearchResult[] = [];
	pillProps: ISearchPillProps | null | undefined = null;
	isSearching = false;
	showResults = false;
	alwaysHideResults = false;
	selectedResults: ISearchResult[] = [];

	defaultPillProps: ISearchPillProps | undefined;

	searchCallback: TSearchCallback | undefined;
	openRecordCallback: TResultItemClickCallback | undefined;
	onClearCallback: TOnClearCallback | undefined;
	onSelectedResultsCallback: TSelectedItemChangeCallback | undefined;
	updateSearchActionCallback: TSearchActionCallback;

	constructor(updateSearchActionCallback: TSearchActionCallback, props: ISearchProps) {
		makeObservable(this, {
			term: observable,
			results: observable.ref,
			pillProps: observable,
			isSearching: observable,
			showResults: observable,
			alwaysHideResults: observable,
			selectedResults: observable,
			reset: action,
			setTerm: action,
			setupPillProps: action,
			resetPillProps: action,
			setIsSearching: action,
			displayResults: action,
			updateResults: action,
			updatePillProps: action,
			fetchResults: action,
			hideResults: action,
			clickRecord: action,
			setSelectedResults: action,
			addSelectedResult: action,
			removeSelectedResult: action,
			clearSelectedResults: action,
			hasSelectedResults: computed,
		});

		this.searchCallback = props.searchCallback;
		this.openRecordCallback = props.onResultItemClick;
		this.updateSearchActionCallback = updateSearchActionCallback;
		this.onClearCallback = props.onClearCallback;
		this.onSelectedResultsCallback = props.onSelectedResultsChange;
		this.selectedResults = props.selectedResults || [];
		this.term = props.term || '';
		this.defaultPillProps = props.pillProps;
		this.pillProps = props.pillProps;
		this.alwaysHideResults = props.alwaysHideResults || false;
	}

	reset = () => {
		this.term = '';
		this.results = [];
		this.resetPillProps();
		if (this.searchCallback) {
			this.searchCallback('');
		}
		if (this.onClearCallback) {
			this.onClearCallback();
		}
	};

	setTerm = (term: string) => {
		this.term = term;
		this.updateSearchActionCallback('UPDATED_TERM');
		if (common.commonUtils.isNullOrEmpty(term) && this.onClearCallback) {
			this.onClearCallback();
		}
	};

	setupPillProps = (pillProps: ISearchPillProps | undefined) => {
		this.defaultPillProps = pillProps;
		this.pillProps = pillProps;
		this.updateSearchActionCallback('UPDATED_PILL_PROPS');
	};

	resetPillProps = () => {
		this.pillProps = this.defaultPillProps;
		this.updateSearchActionCallback('UPDATED_PILL_PROPS');
	};

	setIsSearching = (isSearching: boolean) => {
		this.isSearching = isSearching;
		this.updateSearchActionCallback('UPDATED_SEARCH_STATUS');
	};

	displayResults = (override?: boolean) => {
		this.showResults = !this.alwaysHideResults && (override !== undefined ? override : this.results.length > 0);
		this.updateSearchActionCallback('UPDATED_RESULT_DISPLAY');
	};

	updateResults = (results: ISearchResult[]) => {
		this.results = results;
		this.updateSearchActionCallback('UPDATED_RESULTS');
	};

	updatePillProps = (pillProps: ISearchPillProps) => {
		this.pillProps = pillProps;
		this.updateSearchActionCallback('UPDATED_PILL_PROPS');
	};

	fetchResults = async () => {
		if (this.searchCallback) {
			const response = await this.searchCallback(this.term);
			if (response && response.term === this.term) {
				if (response.records) {
					this.updateResults(response.records);
				}
				if (response.pillProps) {
					this.updatePillProps(
						Object.assign({}, this.pillProps || {}, response.pillProps) as ISearchPillProps,
					);
				}
			}
			this.displayResults();
			this.setIsSearching(false);
			this.updateSearchActionCallback('UPDATED_RESULTS');
		}
	};

	hideResults = () => {
		this.displayResults(false);
	};

	clickRecord = (record: any) => {
		this.hideResults();
		this.addSelectedResult(record);
		if (this.openRecordCallback) {
			this.openRecordCallback(record);
		}
	};

	setSelectedResults = (selectedResults: any) => {
		if (JSON.stringify(this.selectedResults) !== JSON.stringify(selectedResults)) {
			this.selectedResults = selectedResults;
		}
	};

	addSelectedResult = (record: any) => {
		if (!this.selectedResults.some((item) => item.id === record.id)) {
			this.selectedResults.push(record);
			this.setTerm('');
			if (this.onSelectedResultsCallback) {
				this.onSelectedResultsCallback(this.selectedResults);
			}
		}
	};

	removeSelectedResult = (record: any) => {
		const filteredResults = [...this.selectedResults].filter((item) => item.id !== record.id);
		this.selectedResults = filteredResults;
		if (this.onSelectedResultsCallback) {
			this.onSelectedResultsCallback(this.selectedResults);
		}
	};

	clearSelectedResults() {
		this.selectedResults = [...[]];
		if (this.onSelectedResultsCallback) {
			this.onSelectedResultsCallback(this.selectedResults);
		}
	}

	get hasSelectedResults(): boolean {
		return this.selectedResults && this.selectedResults.length > 0;
	}
}
